"use client";

import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { APIClientProvider, type ApiConfig } from "~/api-client";
import { CartsProvider } from "~/hooks/cart";
import { CurrencyProvider } from "~/hooks/currency";
import { NotificationProvider } from "~/hooks/notifications";
import { ThemeProvider } from "~/hooks/theme";
import { Theme } from "~/theme";
import { StripeProvider } from "./StripeProvider";

// TypeScript type declaration for TanStack Query Chrome DevTools
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

export function SiteProvider(props: {
  apiConfig: ApiConfig;
  stripePublishableKey: string;
  children: React.ReactNode;
}) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  const theme = new Theme();

  return (
    <QueryClientProvider client={queryClient}>
      <APIClientProvider apiConfig={props.apiConfig}>
        <StripeProvider stripePublishableKey={props.stripePublishableKey}>
          <CurrencyProvider currency="USD">
            <CartsProvider>
              <ThemeProvider theme={theme}>
                <NotificationProvider>{props.children}</NotificationProvider>
              </ThemeProvider>
            </CartsProvider>
          </CurrencyProvider>
        </StripeProvider>
      </APIClientProvider>
    </QueryClientProvider>
  );
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Disable automatic refetching by default to match SWR's behavior in this codebase
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        retry: 1,
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;
const TANSTACK_DEV_TOOLS_URL_WHITELIST = ["staging-usa.loke.app"];
const VERCEL_PREVIEW_URL_PATTERN = /^client-jets-.*-loke\.vercel\.app$/;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();

      // Expose QueryClient globally for TanStack Query Chrome DevTools
      // Only expose in non-production environments or whitelisted URLs

      const isNotProduction = process.env.NODE_ENV !== "production";
      const isWhitelistedUrl = TANSTACK_DEV_TOOLS_URL_WHITELIST.includes(
        window.location.hostname,
      );
      const isVercelPreview = VERCEL_PREVIEW_URL_PATTERN.test(
        window.location.hostname,
      );

      if (isNotProduction || isWhitelistedUrl || isVercelPreview) {
        window.__TANSTACK_QUERY_CLIENT__ = browserQueryClient;
      }
    }
    return browserQueryClient;
  }
}
